import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, Sparkles, Wand2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { withBackend } from '../utils/api';
import { toast } from 'sonner';

const SUGGESTIONS = [
  'Make it more concise',
  'Add more detail to each slide',
  'Reorder for better flow',
  'Make the tone more casual',
];

export default function OutlinePromptSidebar({ outline, onOutlineUpdate, questionnaireAnswers }) {
  const [prompt, setPrompt] = useState('');
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history]);

  const handleSubmit = async (text) => {
    const instruction = text || prompt.trim();
    if (!instruction || isLoading) return;

    setPrompt('');
    setHistory(prev => [...prev, { role: 'user', content: instruction }]);
    setIsLoading(true);

    try {
      const response = await fetch(withBackend('/api/edit-outline'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outline,
          instruction,
          llm_provider: 'openai',
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to edit outline');
      }

      // Read SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      const newSlides = [];
      let newTitle = outline?.title || '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'title' && event.title) {
                newTitle = event.title;
              }
              if (event.type === 'slide' && event.slide) {
                newSlides.push(event.slide);
              }
            } catch (e) {
              // Skip unparseable events
            }
          }
        }
      }

      if (newSlides.length > 0) {
        onOutlineUpdate({ title: newTitle, slides: newSlides });
        setHistory(prev => [...prev, {
          role: 'bot',
          content: `Updated outline (${newSlides.length} slides).`
        }]);
        toast.success('Outline updated!');
      } else {
        throw new Error('No slides returned from edit');
      }
    } catch (error) {
      console.error('Outline edit error:', error);
      setHistory(prev => [...prev, { role: 'bot', content: `Error: ${error.message}` }]);
      toast.error(error.message || 'Failed to edit outline');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="sticky top-20 border rounded-lg bg-card shadow-sm flex flex-col h-[calc(100vh-12rem)] max-h-[700px]">
      {/* Header */}
      <div className="px-4 py-3 border-b flex items-center gap-2">
        <Wand2 className="w-4 h-4 text-purple-500" />
        <span className="text-sm font-semibold">Edit with AI</span>
      </div>

      {/* Suggestions */}
      {history.length === 0 && (
        <div className="px-3 py-2 border-b">
          <div className="flex flex-wrap gap-1.5">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => handleSubmit(s)}
                disabled={isLoading}
                className="px-2.5 py-1 text-[11px] rounded-full border border-purple-200 dark:border-purple-800
                  bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300
                  hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Chat history */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5 min-h-0">
        {history.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-8">
            Ask AI to modify your outline
          </p>
        )}
        {history.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[90%] rounded-xl px-3 py-2 text-xs ${
                msg.role === 'user'
                  ? 'bg-purple-500 text-white'
                  : 'bg-slate-100 dark:bg-slate-800 text-foreground'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 dark:bg-slate-800 rounded-xl px-3 py-2 text-xs flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
              Editing outline...
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-3 py-2.5 border-t">
        <div className="flex gap-2">
          <Textarea
            ref={textareaRef}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g., Add a slide about..."
            className="min-h-[36px] max-h-[80px] text-xs resize-none"
            rows={1}
            disabled={isLoading}
          />
          <Button
            size="sm"
            onClick={() => handleSubmit()}
            disabled={!prompt.trim() || isLoading}
            className="h-9 w-9 p-0 shrink-0"
          >
            {isLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
